import { useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { TAccount, TChat, TChatMessage } from "../@types";
import { apis, callApi } from "../api";
import { splitStringIntoChunks, validators } from "../utils";
import { useAccount } from "./useAccount";

export type TChatItem = {
	participants: {
		sender: TAccount;
		receiver: TAccount;
	};
	messages: TChatMessage[];
};

export type TChatService = {
	chatList: TChat[];
	chatMessages: {
		[key: string]: TChatItem;
	};
	listeners: (() => void)[];

	addEventListener: (cb: () => void) => void;
	removeEventListener: (cb: () => void) => void;
	emitChanges: () => void;
	saveChatList: (chatList: TChat[]) => void;
	saveChatMessages: (id: string, chatItem: TChatItem) => void;
	clearData: () => void;
};

export const ChatService: TChatService = {
	chatList: [],
	chatMessages: {},
	listeners: [],

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},

	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	saveChatList(chatList) {
		this.chatList = chatList;
	},

	saveChatMessages(id, chatItem) {
		this.chatMessages = { ...this.chatMessages, [id]: chatItem };
	},

	clearData() {
		this.chatList = [];
		this.chatMessages = {};
		this.emitChanges();
	},
};

export function useChatInit(options: {
	chatScreen: boolean;
	getMessages: boolean;
}) {
	type TState = {
		listLoading: boolean;
		itemLoading: boolean;
	};

	const [state, setState] = useState<TState>({
		listLoading: false,
		itemLoading: false,
	});

	const navigate = useNavigate();
	const params = useParams();
	const userId = params.messagesUserId;
	const { account } = useAccount();

	useEffect(() => {
		const getListMessage = async () => {
			try {
				setState((prevState) => ({
					...prevState,
					listLoading: true,
				}));

				const response = await callApi({
					method: "GET",
					url: apis.MESSAGES,
				});

				if (!response) {
					return;
				}

				ChatService.saveChatList(
					response.sort(
						(chat1: TChat, chat2: TChat) =>
							new Date(chat2.recentMessage.createdAt).getTime() -
							new Date(chat1.recentMessage.createdAt).getTime(),
					),
				);
				ChatService.emitChanges();
			} catch (error) {
			} finally {
				setState((prevState) => ({
					...prevState,
					listLoading: false,
				}));
			}
		};

		if (
			((!location.pathname.startsWith("/messages") && !options.chatScreen) ||
				options.chatScreen) &&
			account
		) {
			getListMessage();
		}
	}, [account, options.chatScreen]);

	useEffect(() => {
		const getMessages = async (id: string) => {
			try {
				setState((prevState) => ({
					...prevState,
					itemLoading: true,
				}));
				const response = await callApi({
					method: "GET",
					url: `${apis.MESSAGES_BY_USERS}/${id}`,
				});

				if (!response) {
					return;
				}

				const { messages, participants } = response;

				if (messages.length > 0) {
					const lastMessage: TChatMessage = messages[messages.length - 1];
					const newChatList = ChatService.chatList.filter(
						(chatItem) =>
							[
								chatItem.recentMessage.senderId,
								chatItem.recentMessage.receiverId,
							]
								.sort()
								.join(",") !==
							[lastMessage.senderId, lastMessage.receiverId].sort().join(","),
					);

					let isRead = false;
					if (lastMessage.senderId !== account?.id) {
						isRead = true;
					}

					newChatList.push({
						isRead,
						recentMessage: lastMessage,
						ChannelID: lastMessage.ChannelID,
					});

					newChatList.sort(
						(chat1, chat2) =>
							new Date(chat2.recentMessage.createdAt).getTime() -
							new Date(chat1.recentMessage.createdAt).getTime(),
					);
					ChatService.saveChatList(newChatList);
				}
				ChatService.saveChatMessages(id, {
					participants,
					messages: messages.reverse(),
				});
				ChatService.emitChanges();
			} catch (error) {
				navigate("/messages", { replace: true });
			} finally {
				setState((prevState) => ({
					...prevState,
					itemLoading: false,
				}));
			}
		};
		if (userId && options.getMessages) {
			const isUuidV4 = validators.uuidV4.test(userId);
			if (!isUuidV4) {
				navigate("/messages", { replace: true });
				return;
			}
			getMessages(userId);
		}
	}, [account?.id, navigate, options.getMessages, userId]);

	return { listLoading: state.listLoading, itemLoading: state.itemLoading };
}

export function useChat() {
	const { account } = useAccount();
	type TState = {
		chatList: TChat[];
		chatMessages: {
			[key: string]: TChatItem;
		};
	};
	const [state, setState] = useState<TState>({
		chatList: ChatService.chatList,
		chatMessages: ChatService.chatMessages,
	});

	useEffect(() => {
		const updateData = () => {
			setState((prevState) => ({
				...prevState,
				chatList: ChatService.chatList,
				chatMessages: ChatService.chatMessages,
			}));
		};

		ChatService.addEventListener(updateData);

		return () => {
			ChatService.removeEventListener(updateData);
		};
	}, []);

	const hasUnreadMessage = state.chatList.some(
		(chatItem) =>
			!chatItem.isRead && chatItem.recentMessage.senderId !== account?.id,
	);

	const splitMessageAndSend = async (data: {
		receiverId: string;
		body: string;
	}) => {
		const splittedMessages = splitStringIntoChunks(data.body, 1000);
		for (let i = 0; i < splittedMessages.length; i++) {
			await sendMessage({
				receiverId: data.receiverId,
				body: splittedMessages[i].trim(),
			});
		}
	};

	const sendMessage = async (data: { receiverId: string; body: string }) => {
		try {
			const response = await callApi({
				method: "POST",
				url: apis.MESSAGES_CHAT,
				body: data,
			});

			if (!response) {
				return;
			}

			const message = response.chat;
			const newChatList: TChat[] = ChatService.chatList.filter(
				(chatItem) =>
					[chatItem.recentMessage.senderId, chatItem.recentMessage.receiverId]
						.sort()
						.join(",") !==
					[message.senderId, message.receiverId].sort().join(","),
			);

			newChatList.push({
				isRead: false,
				recentMessage: message,
				ChannelID: message.ChannelID,
			});

			newChatList.sort(
				(chat1, chat2) =>
					new Date(chat2.recentMessage.createdAt).getTime() -
					new Date(chat1.recentMessage.createdAt).getTime(),
			);
			ChatService.saveChatList(newChatList);
			ChatService.saveChatMessages(data.receiverId, {
				...ChatService.chatMessages[data.receiverId],
				messages: [
					message,
					...ChatService.chatMessages[data.receiverId].messages,
				],
			});
			ChatService.emitChanges();
		} catch (error) {}
	};

	return { ...state, hasUnreadMessage, splitMessageAndSend };
}
